import type { TaskDetailModalStore } from "@components/widgets/Modals/ModalCommunication/store/task-detail-store";
import type { RenderMode } from "@components/widgets/Modals/ModalCommunication/store/types";
import { TaskTabIdEnum } from "@src/constants/tasks";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { debounce } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { createRef, type MouseEvent, type RefObject } from "react";

const RENDER_MODE_CACHE_KEY = "task_detail_render_mode";
const DIVIDER_POSITION_CACHE_KEY = "task_detail_divider_position";
const MIN_PANEL_PERCENT = 20;
const MAX_PANEL_PERCENT = 80;

/** Drawer or modal rendering, and the resizable split of the modal. */
export class TaskDetailLayoutStore {
  @observable.ref renderMode: RenderMode = "drawer";
  @observable.ref leftPanelWidth = 50;
  leftModalPanelRef = createRef<HTMLDivElement>();
  isResizing = new BooleanState();

  constructor(private readonly detailStore: TaskDetailModalStore) {
    makeObservable(this);
    this.loadFromCache();
  }

  @computed get isDrawerMode(): boolean {
    return this.renderMode === "drawer";
  }

  @computed get leftPanelWidthPercentage(): string {
    return `${this.leftPanelWidth}%`;
  }

  @computed get rightPanelWidthPercentage(): string {
    return `${100 - this.leftPanelWidth}%`;
  }

  @action.bound
  toggleRenderMode(): void {
    this.setRenderMode(this.isDrawerMode ? "modal" : "drawer");
  }

  @action.bound
  setRenderMode(mode: RenderMode): void {
    this.renderMode = mode;
    writeCache(RENDER_MODE_CACHE_KEY, mode);

    // The modal shows the communication in its right panel, not as a tab
    const { activeTabId, commentChanged } = this.detailStore;
    if (mode === "modal" && activeTabId.value === TaskTabIdEnum.Communication) {
      activeTabId.set(TaskTabIdEnum.Description);
      return;
    }

    if (!commentChanged) return;
    activeTabId.set(TaskTabIdEnum.Communication);
  }

  @action.bound
  startResize(): void {
    this.isResizing.on();
  }

  @action.bound
  stopResize(): void {
    this.isResizing.off();
  }

  @action.bound
  resize(event: MouseEvent, containerRef: RefObject<HTMLDivElement>): void {
    if (!this.isResizing.value || !containerRef.current) return;

    const { left, width } = containerRef.current.getBoundingClientRect();
    const percent = ((event.clientX - left) / width) * 100;
    this.leftPanelWidth = Math.min(
      Math.max(percent, MIN_PANEL_PERCENT),
      MAX_PANEL_PERCENT,
    );
    this.saveDividerPosition(this.leftPanelWidth);
  }

  private saveDividerPosition = debounce((position: number) => {
    writeCache(DIVIDER_POSITION_CACHE_KEY, position.toString());
  }, 500);

  @action
  private loadFromCache(): void {
    const mode = readCache(RENDER_MODE_CACHE_KEY);
    if (mode === "drawer" || mode === "modal") this.renderMode = mode;

    const position = Number.parseFloat(
      readCache(DIVIDER_POSITION_CACHE_KEY) ?? "",
    );
    if (!Number.isNaN(position)) this.leftPanelWidth = position;
  }
}

// Storage can be blocked (e.g. private mode), the defaults then apply
function readCache(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeCache(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not persisted, the layout still works for this session
  }
}
